import { useEffect } from 'react';

/** Params for keyboard and wheel shortcuts - accept dependencies via params */
export interface UseKeyboardAndWheelShortcutsParams {
  containerRef: React.RefObject<HTMLDivElement | null>;
  lastWheelTimeRef: React.MutableRefObject<number>;
  lastPlacedAnnotationsRef: React.MutableRefObject<string[]>;
  annotationsRef: React.MutableRefObject<any[]>;

  selectedAnnotation: string | null;
  selectedAnnotations: Set<string>;
  deleteSelected: () => void;
  snapshotSelection: { startX: number; startY: number; endX: number; endY: number } | null;
  showSnapshotActions: boolean;
  handleCancelSnapshot: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  zoomAtPoint: (factor: number, clientX: number, clientY: number) => void;
  isWireRunMode: boolean;
  countingModeActive: boolean | undefined;
  activePlacementComponent: { name: string; symbol: string; color: string; symbolType?: string } | null | undefined;
  addToHistory: (ann: any[]) => void;
  wireRunExtendMode: { annotationId: string; endpoint: 'start' | 'end' } | null;
  wireRunEditContextMenu: any;

  setWireRunEditContextMenu: (v: any) => void;
  setWireRunExtendMode: (v: { annotationId: string; endpoint: 'start' | 'end' } | null) => void;
  setWireRunExtendPreviewPoint: (v: { x: number; y: number } | null) => void;
  setWireRunVertexDrag: (v: { annotationId: string; pointIndex: number } | null) => void;
  setAnnotations: (ann: any[] | ((prev: any[]) => any[])) => void;
  setSelectedAnnotation: (id: string | null) => void;
  setPanX: (fn: (prev: number) => number) => void;
  setPanY: (fn: (prev: number) => number) => void;
  persistDeletedAnnotations: (ids: string[], types?: string[]) => void | Promise<void>;
  onStopCounting?: () => void;
  cancelActivePlacementDrawing?: () => void;
  onCopyRectangles?: () => boolean;
  onPasteRectangles?: () => boolean | Promise<boolean>;
}

export function useKeyboardAndWheelShortcuts(params: UseKeyboardAndWheelShortcutsParams) {
  const {
    containerRef,
    lastWheelTimeRef,
    lastPlacedAnnotationsRef,
    annotationsRef,
    selectedAnnotation,
    selectedAnnotations,
    deleteSelected,
    snapshotSelection,
    showSnapshotActions,
    handleCancelSnapshot,
    zoomIn,
    zoomOut,
    resetZoom,
    zoomAtPoint,
    isWireRunMode,
    countingModeActive,
    activePlacementComponent,
    addToHistory,
    wireRunExtendMode,
    wireRunEditContextMenu,
    setWireRunEditContextMenu,
    setWireRunExtendMode,
    setWireRunExtendPreviewPoint,
    setWireRunVertexDrag,
    setAnnotations,
    setSelectedAnnotation,
    setPanX,
    setPanY,
    persistDeletedAnnotations,
    onStopCounting,
    cancelActivePlacementDrawing,
    onCopyRectangles,
    onPasteRectangles,
  } = params;

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      const isTypingInField = () => {
        const activeEl = document.activeElement as HTMLElement | null;
        return !!activeEl?.closest(
          'input, textarea, [contenteditable="true"], [role="textbox"], [role="combobox"]'
        );
      };

      if (e.key === 'Escape' && wireRunEditContextMenu) {
        e.preventDefault();
        setWireRunEditContextMenu(null);
        return;
      }

      if (e.key === 'Escape' && wireRunExtendMode) {
        e.preventDefault();
        setWireRunExtendMode(null);
        setWireRunExtendPreviewPoint(null);
        setWireRunVertexDrag(null);
        return;
      }

      // Handle Ctrl/Cmd+C for rectangle copy
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'c') {
        if (isTypingInField()) return;
        const handled = onCopyRectangles?.() ?? false;
        if (handled) {
          e.preventDefault();
        }
        return;
      }

      // Handle Ctrl/Cmd+V for rectangle paste
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'v') {
        if (isTypingInField()) return;
        const handled = (await Promise.resolve(onPasteRectangles?.())) ?? false;
        if (handled) {
          e.preventDefault();
        }
        return;
      }

      // Handle Escape to cancel snapshot selection
      if (e.key === 'Escape' && (snapshotSelection || showSnapshotActions)) {
        handleCancelSnapshot();
        return;
      }

      // Exit selected-component placement mode
      if (e.key === 'Escape' && countingModeActive && activePlacementComponent) {
        e.preventDefault();
        cancelActivePlacementDrawing?.();
        onStopCounting?.();
        return;
      }

      // Handle Ctrl+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();

        // If in wire run mode and actively drawing, undo last wire point
        if (isWireRunMode && (window as any).handDrawnGetWirePointCount) {
          const pointCount = (window as any).handDrawnGetWirePointCount();
          if (pointCount > 0) {
            console.log('⬅️ Ctrl+Z: Undoing last wire point');
            if ((window as any).handDrawnUndoWirePoint) {
              (window as any).handDrawnUndoWirePoint();
            }
            return;
          }
        }

        // If in counting mode and we have placed annotations, remove the last one
        if (countingModeActive && activePlacementComponent && lastPlacedAnnotationsRef.current.length > 0) {
          // Pop the last annotation ID from the stack
          const lastAnnotationId = lastPlacedAnnotationsRef.current.pop();

          if (lastAnnotationId) {
            console.log('⬅️ Ctrl+Z: Undoing last placed component:', lastAnnotationId, `(${lastPlacedAnnotationsRef.current.length} more in undo stack)`);

            // Find and remove the last placed annotation
            const currentAnnotations = annotationsRef.current;
            const annotationToRemove = currentAnnotations.find(a => a.id === lastAnnotationId);

            if (annotationToRemove) {
              const deletedIds = new Set<string>([lastAnnotationId]);
              currentAnnotations.forEach((annotation: any) => {
                if (annotation.type !== 'piping') return;
                const startDeleted = annotation.startAnnotationId && deletedIds.has(annotation.startAnnotationId);
                const endDeleted = annotation.endAnnotationId && deletedIds.has(annotation.endAnnotationId);
                if (startDeleted || endDeleted) deletedIds.add(annotation.id);
              });

              const newAnnotations = currentAnnotations.filter((a: any) => !deletedIds.has(a.id));
              annotationsRef.current = newAnnotations;
              setAnnotations(newAnnotations);
              addToHistory(newAnnotations);
              setSelectedAnnotation(null);

              console.log(`✅ Removed ${annotationToRemove.component_type} annotation`);

              const deletedComponentTypes = annotationToRemove.component_type
                ? [annotationToRemove.component_type]
                : [];
              void persistDeletedAnnotations(Array.from(deletedIds), deletedComponentTypes);
            }
          }
        }

        return;
      }

      // Handle delete/backspace for deleting selected annotations
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // If user is typing in search/input UI, do not delete annotations
        if (isTypingInField()) return;

        if (selectedAnnotation || selectedAnnotations.size > 0) {
          e.preventDefault();
          deleteSelected();
        }
      }
      // Handle zoom shortcuts
      if (e.ctrlKey || e.metaKey) {
        const now = Date.now();
        if (e.key === '=' || e.key === '+' || e.key === '-' || e.key === '_') {
          if (now - lastWheelTimeRef.current < 30) return;
          lastWheelTimeRef.current = now;
        }

        if (e.key === '=' || e.key === '+') {
          e.preventDefault();
          zoomIn();
        } else if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          zoomOut();
        } else if (e.key === '0') {
          e.preventDefault();
          resetZoom();
        }
      }
    };

    const handleWheel = (e: WheelEvent) => {
      const container = containerRef.current;
      if (!container || !container.contains(e.target as Node)) return;

      e.preventDefault();

      if (e.ctrlKey || e.metaKey) {
        // Throttle zoom events to prevent "shifting" during rapid scroll
        const now = Date.now();
        if (now - lastWheelTimeRef.current < 50) return; // Limit zoom speed to prevent shifting
        lastWheelTimeRef.current = now;

        // Zoom
        const factor = e.deltaY > 0 ? 0.8 : 1.25;
        zoomAtPoint(factor, e.clientX, e.clientY);
      } else if (e.shiftKey) {
        // Shift + wheel pans horizontally (Photoshop-like behavior)
        const horizontalDelta = Math.abs(e.deltaX) > 0 ? e.deltaX : e.deltaY;
        setPanX(prev => prev - horizontalDelta);
      } else {
        // Pan
        setPanX(prev => prev - e.deltaX);
        setPanY(prev => prev - e.deltaY);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    // Listen on document in capture phase so we run before the scrollable container sees the event
    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('wheel', handleWheel, true);
    };
  }, [
    selectedAnnotation,
    selectedAnnotations,
    deleteSelected,
    snapshotSelection,
    showSnapshotActions,
    handleCancelSnapshot,
    zoomIn,
    zoomOut,
    resetZoom,
    zoomAtPoint,
    isWireRunMode,
    countingModeActive,
    activePlacementComponent,
    addToHistory,
    wireRunExtendMode,
    wireRunEditContextMenu,
    setWireRunEditContextMenu,
    setWireRunExtendMode,
    setWireRunExtendPreviewPoint,
    setWireRunVertexDrag,
    setAnnotations,
    setSelectedAnnotation,
    setPanX,
    setPanY,
    persistDeletedAnnotations,
    onStopCounting,
    cancelActivePlacementDrawing,
    onCopyRectangles,
    onPasteRectangles,
    containerRef,
    lastWheelTimeRef,
    lastPlacedAnnotationsRef,
    annotationsRef,
  ]);
}
